<template>
  <div class="space-y-4">
    <!-- 顶部概览 -->
    <div class="glass-card rounded-2xl border border-slate-700/70 p-5 flex flex-col gap-4 shadow-lg shadow-blue-900/20">
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <div class="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-400/45 via-cyan-400/35 to-purple-500/35 border border-white/12 flex items-center justify-center shadow-lg shadow-blue-700/30">
            <i class="ph-bold ph-database text-white text-lg drop-shadow-[0_6px_12px_rgba(59,130,246,0.55)]"></i>
          </div>
          <div>
            <p class="text-xs text-slate-400">MySQL 数据库监控</p>
            <h2 class="text-lg font-bold text-white">全维度健康概览</h2>
          </div>
        </div>
        <div class="flex items-center gap-3 text-sm">
          <span class="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/60 text-slate-200">
            <span class="w-2 h-2 rounded-full" :class="statusDot"></span>
            {{ statusText }}
          </span>
          <div class="text-xs text-slate-400 flex items-center gap-1">
            <i class="ph-bold ph-clock"></i>
            运行时长: {{ uptimeText }}
          </div>
        </div>
      </div>

      <div v-if="loading" class="text-center py-6 text-slate-400">加载中...</div>

      <div v-else-if="!monitorData" class="text-center py-6 text-slate-500">
        暂无监控数据
      </div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div
          v-for="card in overviewCards"
          :key="card.id"
          class="relative rounded-2xl border border-slate-700/70 bg-slate-900/60 p-4 overflow-hidden shadow-sm shadow-blue-900/20 transition-all duration-300 group hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-900/40"
        >
          <div class="absolute inset-0 opacity-0 group-hover:opacity-70 transition-opacity duration-300" :class="card.bg"></div>
          <div class="relative flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div
                class="w-11 h-11 rounded-2xl flex items-center justify-center text-white/90 shadow-lg ring-1 ring-white/12 bg-gradient-to-br"
                :class="card.iconBg"
              >
                <i :class="card.icon" class="drop-shadow-[0_6px_12px_rgba(59,130,246,0.35)]"></i>
              </div>
              <div>
                <p class="text-xs text-slate-400">{{ card.subtitle }}</p>
                <p class="text-sm font-semibold text-white">{{ card.title }}</p>
              </div>
            </div>
            <div class="text-right">
              <p :class="['text-2xl font-bold leading-tight', card.color]">
                {{ card.value }}
                <span v-if="card.unit" class="text-base font-semibold text-slate-300">{{ card.unit }}</span>
              </p>
              <p class="text-[11px] text-slate-400 mt-1">{{ card.tip }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 关键维度卡片 -->
    <div v-if="monitorData" class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div v-for="section in sections" :key="section.id" class="glass-card rounded-2xl border border-slate-700/70 p-4 shadow-lg shadow-blue-900/20">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <div
              class="w-9 h-9 rounded-xl flex items-center justify-center text-white/90 shadow-lg ring-1 ring-white/12 bg-gradient-to-br"
              :class="section.iconBg"
            >
              <i :class="section.icon" class="drop-shadow-[0_5px_10px_rgba(59,130,246,0.3)]"></i>
            </div>
            <div>
              <p class="text-xs text-slate-400">{{ section.subtitle }}</p>
              <p class="text-sm font-semibold text-white">{{ section.title }}</p>
            </div>
          </div>
          <span v-if="section.tip" class="text-[11px] text-slate-400">{{ section.tip }}</span>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div
            v-for="item in section.items"
            :key="item.id"
            class="rounded-xl border border-slate-700/60 bg-slate-900/50 p-3 relative overflow-hidden transition-all duration-300 group hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-blue-900/30"
          >
            <div class="flex items-center justify-between">
              <p class="text-xs text-slate-400 flex items-center gap-1">
                <span class="w-1.5 h-1.5 rounded-full" :class="item.statusDot"></span>
                {{ item.label }}
              </p>
              <span v-if="item.badge" class="text-[10px] px-2 py-0.5 rounded-full border" :class="item.badgeClass">{{ item.badge }}</span>
            </div>
            <p class="mt-1 text-lg font-bold" :class="item.color">
              {{ item.value }} <span v-if="item.unit" class="text-sm text-slate-400 font-semibold">{{ item.unit }}</span>
            </p>
            <p class="text-[11px] text-slate-500 mt-1">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  monitorData: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  formatFileSize: { type: Function, default: (v) => `${v || 0}` }
});

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const clampPct = (v) => {
  const n = num(v, 0);
  return Math.min(100, Math.max(0, n));
};

const fmt = (v, digits = 1) => clampPct(v).toFixed(digits);

const uptimeText = computed(() => props.monitorData?.uptime || '--');
const statusText = computed(() => {
  if (!props.monitorData) return '未知';
  if (props.monitorData.status === 'online' || props.monitorData.status === 'UP') return '在线';
  return '异常';
});
const statusDot = computed(() => {
  if (!props.monitorData) return 'bg-slate-500';
  if (props.monitorData.status === 'online' || props.monitorData.status === 'UP') return 'bg-emerald-400 animate-pulse';
  return 'bg-red-500';
});

const overviewCards = computed(() => {
  const d = props.monitorData || {};
  return [
    {
      id: 'conn',
      title: '连接使用率',
      subtitle: 'Threads / max_connections',
      value: fmt(d.connUsage ?? d.connectionUsage ?? 0),
      unit: '%',
      tip: `活跃 ${d.threadsRunning ?? d.activeConnections ?? 0} / 总 ${d.threadsConnected ?? d.totalConnections ?? 0}`,
      color: (num(d.connUsage ?? d.connectionUsage ?? 0)) > 90 ? 'text-red-400' : (num(d.connUsage ?? d.connectionUsage ?? 0)) > 75 ? 'text-amber-300' : 'text-emerald-300',
      icon: 'ph-bold ph-plugs-connected text-white',
      iconBg: 'bg-emerald-500/40 ring-1 ring-emerald-200/40',
      bg: 'bg-gradient-to-br from-emerald-500/10 via-cyan-500/5 to-transparent'
    },
    {
      id: 'qps',
      title: 'QPS / TPS',
      subtitle: '查询/事务每秒',
      value: `${num(d.qps, 0).toFixed(1)} / ${num(d.tps, 0).toFixed(1)}`,
      tip: `Slow ${d.slowQueries ?? d.slowQueryCount ?? 0}/m`,
      color: (num(d.slowRate ?? 0)) > 1 ? 'text-amber-300' : 'text-emerald-300',
      icon: 'ph-bold ph-activity text-white',
      iconBg: 'bg-blue-500/40 ring-1 ring-blue-200/40',
      bg: 'bg-gradient-to-br from-blue-500/10 via-cyan-500/6 to-transparent'
    },
    {
      id: 'io',
      title: '磁盘使用率',
      subtitle: '数据目录',
      value: fmt(d.diskUsagePct ?? d.dataDirUsage ?? 0),
      unit: '%',
      tip: `${props.formatFileSize(d.dataDirUsed || d.diskUsed)} / ${props.formatFileSize(d.dataDirSize || d.diskTotal)}`,
      color: (num(d.diskUsagePct ?? d.dataDirUsage ?? 0)) > 90 ? 'text-red-400' : (num(d.diskUsagePct ?? d.dataDirUsage ?? 0)) > 80 ? 'text-amber-300' : 'text-emerald-300',
      icon: 'ph-bold ph-hard-drive text-white',
      iconBg: 'bg-purple-500/40 ring-1 ring-purple-200/40',
      bg: 'bg-gradient-to-br from-purple-500/10 via-indigo-500/6 to-transparent'
    }
  ];
});

const sections = computed(() => {
  const d = props.monitorData || {};
  const safePct = (value, danger = 90, warn = 80) => {
    if (value >= danger) return { dot: 'bg-red-400', color: 'text-red-400' };
    if (value >= warn) return { dot: 'bg-amber-300', color: 'text-amber-300' };
    return { dot: 'bg-emerald-400', color: 'text-emerald-300' };
  };

  const connPct = clampPct(d.connUsage ?? d.connectionUsage ?? 0);
  const bufferHit = clampPct(d.bufferHitRatio ?? d.innodbHitRate ?? d.bufferPoolHitRate ?? 0);
  const rowLockWaits = num(d.rowLockWaits ?? d.innodbRowLockWaits ?? 0, 0);
  const rowLockTime = num(d.rowLockTime ?? d.innodbRowLockTime ?? 0, 0);
  const replDelay = num(d.replicationDelay ?? d.replicationLag ?? 0, 0);
  const replOk = d.replicationStatus === 'OK' || d.replicationStatus === 'ON';

  return [
    {
      id: 'availability',
      title: '基础可用性',
      subtitle: '存活/复制/重启',
      icon: 'ph-bold ph-shield-check text-white',
      iconBg: 'bg-emerald-500/40 ring-1 ring-emerald-200/40',
      tip: `重启 ${d.restartCount ?? 0} 次 / 24h`,
      items: [
        {
          id: 'alive',
          label: '实例存活',
          value: d.status === 'online' || d.status === 'UP' ? '在线' : '异常',
          desc: '3306 连通 + 登录',
          statusDot: d.status === 'online' || d.status === 'UP' ? 'bg-emerald-400' : 'bg-red-400',
          color: d.status === 'online' || d.status === 'UP' ? 'text-emerald-300' : 'text-red-400'
        },
        {
          id: 'repl',
          label: '主从复制',
          value: replOk ? '正常' : '异常',
          desc: `延迟 ${replDelay || 0}s`,
          statusDot: replOk ? 'bg-emerald-400' : 'bg-red-400',
          color: replOk ? 'text-emerald-300' : 'text-red-400',
          badge: replDelay ? `${replDelay}s` : '',
          badgeClass: 'border-slate-600 text-slate-300'
        },
        {
          id: 'reject',
          label: '连接拒绝',
          value: d.connRejects ?? d.connectionRejects ?? 0,
          desc: 'Too many connections / auth fail',
          statusDot: (d.connRejects ?? 0) > 0 ? 'bg-amber-300' : 'bg-emerald-400',
          color: (d.connRejects ?? 0) > 0 ? 'text-amber-300' : 'text-emerald-300'
        },
        {
          id: 'uptime',
          label: '运行时长',
          value: d.uptime || '--',
          desc: '持续运行时间',
          statusDot: 'bg-emerald-400',
          color: 'text-slate-200'
        }
      ]
    },
    {
      id: 'performance',
      title: '性能与连接',
      subtitle: 'QPS/TPS/慢查/连接',
      icon: 'ph-bold ph-activity text-white',
      iconBg: 'bg-blue-500/40 ring-1 ring-blue-200/40',
      tip: '关键性能指标',
      items: [
        {
          id: 'qps',
          label: 'QPS / TPS',
          value: `${num(d.qps,0).toFixed(1)} / ${num(d.tps,0).toFixed(1)}`,
          desc: 'Queries / Transactions',
          statusDot: 'bg-emerald-400',
          color: 'text-emerald-300'
        },
        {
          id: 'slow',
          label: '慢查询',
          value: d.slowQueries ?? d.slowQueryCount ?? 0,
          desc: `慢查率 ${fmt(d.slowRate ?? 0)}%`,
          statusDot: (num(d.slowRate ?? 0)) > 1 ? 'bg-amber-300' : 'bg-emerald-400',
          color: (num(d.slowRate ?? 0)) > 1 ? 'text-amber-300' : 'text-emerald-300'
        },
        {
          id: 'conn',
          label: '连接使用率',
          value: `${fmt(connPct)}%`,
          desc: `活跃 ${d.threadsRunning ?? d.activeConnections ?? 0} / 总 ${d.threadsConnected ?? d.totalConnections ?? 0}`,
          ...safePct(connPct)
        },
        {
          id: 'abort',
          label: '失败/超时',
          value: d.aborted ?? d.abortedConnects ?? 0,
          desc: 'Aborted_connects',
          statusDot: (d.aborted ?? 0) > 0 ? 'bg-amber-300' : 'bg-emerald-400',
          color: (d.aborted ?? 0) > 0 ? 'text-amber-300' : 'text-emerald-300'
        }
      ]
    },
    {
      id: 'innodb',
      title: 'InnoDB 核心',
      subtitle: '缓冲池/锁/IO',
      icon: 'ph-bold ph-cpu text-white',
      iconBg: 'bg-cyan-500/40 ring-1 ring-cyan-200/40',
      tip: '存储引擎健康度',
      items: [
        {
          id: 'hit',
          label: '缓冲命中率',
          value: `${fmt(bufferHit)}%`,
          desc: 'Innodb buffer pool hit',
          ...safePct(100 - bufferHit, 20, 10)
        },
        {
          id: 'dirty',
          label: '脏页比例',
          value: `${fmt(d.dirtyPageRatio ?? d.dirtyPagesPct ?? 0)}%`,
          desc: 'Dirty pages',
          ...safePct(d.dirtyPageRatio ?? d.dirtyPagesPct ?? 0)
        },
        {
          id: 'lock',
          label: '行锁等待',
          value: rowLockWaits,
          desc: `等待时长 ${rowLockTime}ms`,
          statusDot: rowLockWaits > 0 ? 'bg-amber-300' : 'bg-emerald-400',
          color: rowLockWaits > 0 ? 'text-amber-300' : 'text-emerald-300'
        },
        {
          id: 'iops',
          label: '读写 IOPS',
          value: `${fmt(d.readIops ?? d.innodbReadIops ?? 0,0)} / ${fmt(d.writeIops ?? d.innodbWriteIops ?? 0,0)}`,
          desc: 'Read / Write',
          statusDot: 'bg-emerald-400',
          color: 'text-slate-200'
        }
      ]
    },
    {
      id: 'safety',
      title: '数据安全',
      subtitle: '备份/日志/一致性',
      icon: 'ph-bold ph-lock text-white',
      iconBg: 'bg-indigo-500/40 ring-1 ring-indigo-200/40',
      tip: '备份与一致性',
      items: [
        {
          id: 'backup',
          label: '备份状态',
          value: d.backupStatus || '未知',
          desc: d.lastBackupAt ? `上次: ${d.lastBackupAt}` : '按计划检查',
          statusDot: d.backupStatus === 'success' ? 'bg-emerald-400' : 'bg-amber-300',
          color: d.backupStatus === 'success' ? 'text-emerald-300' : 'text-amber-300',
          badge: d.backupSize ? props.formatFileSize(d.backupSize) : '',
          badgeClass: 'border-slate-600 text-slate-300'
        },
        {
          id: 'binlog',
          label: 'Binlog 使用',
          value: `${fmt(d.binlogUsagePct ?? 0)}%`,
          desc: 'binlog 磁盘占用',
          ...safePct(d.binlogUsagePct ?? 0)
        },
        {
          id: 'error',
          label: '错误日志',
          value: d.errorCount ?? d.errorLogs ?? 0,
          desc: '新增错误关键字',
          statusDot: (d.errorCount ?? 0) > 0 ? 'bg-amber-300' : 'bg-emerald-400',
          color: (d.errorCount ?? 0) > 0 ? 'text-amber-300' : 'text-emerald-300'
        },
        {
          id: 'config',
          label: '配置变更',
          value: d.configChanged ? '有变更' : '稳定',
          desc: '关键参数监控',
          statusDot: d.configChanged ? 'bg-amber-300' : 'bg-emerald-400',
          color: d.configChanged ? 'text-amber-300' : 'text-emerald-300'
        }
      ]
    }
  ];
});
</script>

<style scoped>
.glass-card {
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}
</style>
